import type { GetStaticPaths, GetStaticProps } from 'next'

import { getSiteConfiguration } from '@aether/services/sanity-service'
import { setTag } from '@sentry/nextjs'
import { PageResponse, PageBuilderSection } from '@aether/models'
import {
  REVALIDATE_PRODUCT,
  ROUTE_BASE_PRODUCTS,
  ROUTE_INSIDER_MEMBERSHIP,
} from '@aether/configs'
import DefaultPage from '../collections/[slug]/index'
import { getProductPageProps } from '../../helpers/getProductPageProps'
import { getActiveDiscounts } from '@aether/discounts/data-access'

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps<
  PageResponse<PageBuilderSection[]>
> = async ({ params, locale, preview = false }) => {
  const { slug } = params

  if (Array.isArray(slug)) {
    throw new Error('Product slug must be passed as an string. Received array')
  }

  if (slug === 'insider-membership') {
    return {
      redirect: {
        destination: ROUTE_INSIDER_MEMBERSHIP,
        permanent: true,
      },
    }
  }

  const productPath = `/vip${ROUTE_BASE_PRODUCTS}/${slug}`
  setTag('page_path', productPath)
  setTag('page_locale', locale)

  const pageProps = await getProductPageProps({
    slug,
    locale,
    isVip: false,
    preview,
  })

  const productId = pageProps?.productId
  const isVipProduct = pageProps?.shopifyData?.products[productId].vipProduct

  if (!pageProps || isVipProduct) {
    return {
      notFound: true,
      revalidate: REVALIDATE_PRODUCT,
    }
  }

  const siteConfiguration = await getSiteConfiguration(locale, preview)
  const discounts = await getActiveDiscounts()

  return {
    props: {
      ...pageProps,
      siteConfiguration,
      allDiscounts: discounts,
    },
    revalidate: REVALIDATE_PRODUCT,
  }
}

export default DefaultPage
